/**
 * TechnicianDashboard.js
 *
 * PHASED ROLLOUT: Technician role only
 * Activity-focused dashboard for Technician role
 *
 * Phase 1 (Complete): Heading section with role badge and refresh button
 * Phase 2 (Current): Work Queue - Assigned Complaints table
 * Future phases: Maintenance due, overdue alerts, recent activity, stats
 *
 * Props:
 * - token: JWT authentication token
 * - user: Current user object
 * - onNavigateToDevices: Callback to navigate to devices view
 * - onNavigateToAlerts: Callback to navigate to alerts view
 * - onNavigateToComplaints: Callback to navigate to complaints view
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../auth/hooks/useAuth';
import BaseDashboard from './BaseDashboard';
import AssignedDevicesSummary from './AssignedDevicesSummary';
import WorkQueueSection from './WorkQueueSection';
import MaintenanceDueSection from './MaintenanceDueSection';
import PersonalPerformanceWidget from './PersonalPerformanceWidget';
import MaintenanceLoggingModal from '../../../shared/components/modals/MaintenanceLoggingModal';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './Dashboards.css';

/**
 * TechnicianDashboard Component
 *
 * Activity-focused dashboard for Technician role
 *
 * Props:
 * - token: JWT authentication token (optional, uses auth context if not provided)
 * - user: User object (optional, uses auth context if not provided)
 * - onNavigateToComplaints: Callback to navigate to complaints view
 * - onNavigateToDevices: Callback to navigate to devices view
 * - onNavigateToDeviceView: Callback to navigate to specific device view
 *
 * Automatically uses authenticated user's role from auth context
 */
const TechnicianDashboard = ({
  token: providedToken,
  user: providedUser,
  onNavigateToComplaints,
  onNavigateToDevices,
  onNavigateToDeviceView,
  onNavigateToWorkLogs,
}) => {
  // Get authenticated user's role and token from context
  const { token: contextToken, user: contextUser } = useAuth();
  const token = providedToken || contextToken;
  const user = providedUser || contextUser;
  const [assignedComplaints, setAssignedComplaints] = useState([]);
  const [allComplaints, setAllComplaints] = useState([]); // For performance metrics
  const [devices, setDevices] = useState([]);
  const [workHours, setWorkHours] = useState(0);
  const [error, setError] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedMaintenanceType, setSelectedMaintenanceType] = useState(null);
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Fetch assigned complaints - wrapped in useCallback for optimization
  const fetchAssignedComplaints = useCallback(async () => {
    try {
      setError(null);

      // Fetch complaints assigned to the current user
      const response = await axios.get(API_ENDPOINTS.COMPLAINTS.GET_ALL, {
        params: { assigned_to: user?.id },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const complaintsData = response.data || [];

      // Fetch status history for each complaint (needed for response time calculation)
      const complaintsWithHistory = await Promise.all(
        complaintsData.map(async (complaint) => {
          try {
            const historyResponse = await axios.get(
              API_ENDPOINTS.COMPLAINTS.GET_STATUS_HISTORY(complaint.id),
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
            return {
              ...complaint,
              status_history: historyResponse.data?.timeline || []
            };
          } catch (err) {
            console.warn(`Failed to fetch status history for complaint ${complaint.id}:`, err);
            return {
              ...complaint,
              status_history: []
            };
          }
        })
      );

      // Store all complaints with status history for performance metrics calculation
      setAllComplaints(complaintsWithHistory);

      // Filter complaints that are open, acknowledged, or in progress, sorted by creation date
      const filtered = complaintsWithHistory
        .filter(c => ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'].includes(c.status))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setAssignedComplaints(filtered);
    } catch (err) {
      console.error('Error fetching assigned complaints:', err);
      setError('Failed to load work queue');
      setAssignedComplaints([]);
      setAllComplaints([]);
    }
  }, [token, user?.id]);

  // Fetch work hours - wrapped in useCallback for optimization
  const fetchWorkHours = useCallback(async () => {
    try {
      // Try to fetch work hours from a work logs API
      // If this endpoint doesn't exist, we'll default to 0
      const response = await axios.get(API_ENDPOINTS.WORK_LOGS.GET_SUMMARY, {
        params: { assigned_to: user?.id, period: 'week' },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setWorkHours(response.data?.totalHours || 0);
    } catch (err) {
      // API endpoint may not exist yet, default to 0
      console.warn('Work hours endpoint not available:', err.message);
      setWorkHours(0);
    }
  }, [token, user?.id]);

  // Fetch devices - wrapped in useCallback for optimization
  const fetchDevices = useCallback(async () => {
    try {
      // Fetch devices assigned to current user as technician for maintenance due section
      const response = await axios.get(API_ENDPOINTS.DEVICES.GET_ASSIGNED_TO_ME, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDevices(response.data.devices || []);
    } catch (err) {
      console.error('Error fetching assigned devices:', err);
      setDevices([]);
    }
  }, [token]);

  // Fetch assigned complaints and devices on component mount or when user/token changes
  useEffect(() => {
    if (user?.id && token) {
      fetchAssignedComplaints();
      fetchDevices();
      fetchWorkHours();
    }
  }, [fetchAssignedComplaints, fetchDevices, fetchWorkHours, token, user?.id]);

  const handleComplaintClick = (complaint) => {
    // Navigate to complaints view with the specific complaint for status updates
    if (onNavigateToComplaints) {
      onNavigateToComplaints(complaint.id);
    }
  };

  // Refresh data when returning from complaint details
  const handleComplaintUpdate = () => {
    // Refresh complaints and performance metrics
    fetchAssignedComplaints();
  };

  const handleLogWorkClick = (deviceId, maintenanceType) => {
    // Find the device and open the maintenance logging modal
    const device = devices.find(d => d.id === deviceId);
    if (device) {
      setSelectedDevice(device);
      setSelectedMaintenanceType(maintenanceType);
      setShowMaintenanceModal(true);
    }
  };

  const handleViewDeviceClick = (deviceId) => {
    // Navigate to device view page to show device details
    if (onNavigateToDeviceView) {
      onNavigateToDeviceView(deviceId);
    }
  };

  const handleMaintenanceSubmit = (data) => {
    // After successful maintenance logging, refresh the devices list to update due dates
    // The modal returns both maintenanceLog and updatedDevice
    if (data?.updatedDevice) {
    }

    // Refresh the devices list to update all maintenance due dates and counts
    fetchDevices();
    setSelectedDevice(null);
    setSelectedMaintenanceType(null);
  };

  // Manual refresh for all dashboard data - wrapped in useCallback for performance
  const handleDashboardRefresh = useCallback(() => {
    fetchAssignedComplaints();
    fetchDevices();
    fetchWorkHours();
  }, [fetchAssignedComplaints, fetchDevices, fetchWorkHours]);

  const assignedDepartments = user?.departments || [];
  const deviceDepartmentNames = useMemo(() => {
    const names = new Set();
    devices.forEach((device) => {
      if (device.department) {
        names.add(device.department);
      }
    });
    return Array.from(names);
  }, [devices]);

  const departmentsWithoutDevices = useMemo(() => {
    if (assignedDepartments.length === 0) {
      return [];
    }
    const deviceDeptSet = new Set(deviceDepartmentNames);
    return assignedDepartments
      .map((dept) => dept.name)
      .filter((name) => !deviceDeptSet.has(name));
  }, [assignedDepartments, deviceDepartmentNames]);

  const quickLinks = [
    { label: '📊 Performance', id: 'section-performance' },
    { label: '🧾 Work Queue', id: 'section-workQueue' },
    { label: '🖥️ My Devices', id: 'section-assignedDevices' },
    { label: '🛠️ Maintenance Due', id: 'section-maintenanceDue' }
  ];

  return (
    <BaseDashboard
      user={user}
      token={token}
      onRefresh={handleDashboardRefresh}
      emptyDepartmentNames={departmentsWithoutDevices}
    >
      {/* ========================================
          PHASE 2: WORK QUEUE SECTION
          Displays assigned complaints in table format
          ======================================== */}

      {error ? (
        <div className="dashboard-error">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load Work Queue</h3>
          <p>{error}</p>
          <button onClick={fetchAssignedComplaints} className="btn-retry">
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* ========================================
              QUICK NAVIGATION LINKS
              Sticky navigation for Technician dashboard sections
              ======================================== */}
          <div
            className={`admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
          >
            <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
              📍 Quick Navigation
            </div>
            <div className="admin-quick-nav-scroll">
              <div
                className="admin-quick-nav-links"
                style={{
                  display: 'flex',
                  flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}
              >
                {quickLinks.map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    className="admin-quick-nav-link"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById(link.id);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* ========================================
              PERSONAL PERFORMANCE WIDGET
              Engineer's weekly performance metrics (complaints + maintenance)
              ======================================== */}
          <section id="section-performance" style={{ scrollMarginTop: 'calc(var(--app-header-offset) + 12px)' }}>
            <PersonalPerformanceWidget
              complaints={allComplaints}
              devices={devices}
              workHours={workHours}
              token={token}
              user={user}
              onNavigateToComplaints={onNavigateToComplaints}
              onNavigateToDevices={onNavigateToDevices}
              onNavigateToWorkLogs={onNavigateToWorkLogs}
            />
          </section>

          {/* ========================================
              PHASE 2: WORK QUEUE SECTION
              Displays assigned complaints in table format
              ======================================== */}
          <section id="section-workQueue" style={{ scrollMarginTop: 'calc(var(--app-header-offset) + 12px)' }}>
            <WorkQueueSection
              assignedComplaints={assignedComplaints}
              token={token}
              onComplaintClick={handleComplaintClick}
            />
          </section>

          {/* ========================================
              PHASE 4: ASSIGNED DEVICES SUMMARY
              Quick overview of assigned devices without navigation
              ======================================== */}
          <section id="section-assignedDevices" style={{ scrollMarginTop: 'calc(var(--app-header-offset) + 12px)' }}>
            <AssignedDevicesSummary
              devices={devices}
              token={token}
              onViewMyDevices={() => onNavigateToDevices && onNavigateToDevices()}
            />
          </section>

          {/* ========================================
              PHASE 3: MAINTENANCE DUE SECTION
              Displays devices with upcoming or overdue maintenance
              ======================================== */}
          <section id="section-maintenanceDue" style={{ scrollMarginTop: 'calc(var(--app-header-offset) + 12px)' }}>
            <MaintenanceDueSection
              devices={devices}
              token={token}
              onScheduleNow={handleLogWorkClick}
              onViewDevice={handleViewDeviceClick}
            />
          </section>

          {/* Future phases will add sections here */}
          {/* Phase 5: Overdue Alerts */}
          {/* Phase 6: Recent Activity */}
        </>
      )}

      {/* Maintenance Logging Modal */}
      <MaintenanceLoggingModal
        isOpen={showMaintenanceModal}
        device={selectedDevice}
        maintenanceType={selectedMaintenanceType}
        onClose={() => {
          setShowMaintenanceModal(false);
          setSelectedDevice(null);
          setSelectedMaintenanceType(null);
          // Refresh so attachment badges / document_count reflect session uploads kept on cancel
          fetchDevices();
        }}
        onSubmit={handleMaintenanceSubmit}
        token={token}
        user={user}
      />
    </BaseDashboard>
  );
};

export default TechnicianDashboard;
